import React, { useContext, useEffect, useState } from 'react';
import { classNames } from '@utils';
import Accordion from '@components/accordion/Accordion';
import AccordionItemWarranty from '@components/accordion/AccordionItemWarranty';
import TagGroup from '@components/tag/TagGroup';
import ModalLegalConfirmation from '@components/modal/ModalLegalConfirmation';
import WarrantylinkService from '@services/apis/warrantylink.api';
import ProfileContext from '../../context/ProfileContext/index';
import {
  Contract,
  CustomerData,
  REOrder,
  Warrantylink,
  WarrantylinkContract,
  WarrantylinkFormData,
} from '@apis/models';
import { addressToString } from '@services/helpers';
import { formatDateFromString, formatDateToISO8601 } from '@app/helpers/utils';
import AgentApi from '@apis/agent.api';
import { isEmpty } from 'lodash';
import { fireGAEvent } from '@app/core/tracking.service';
import {
  NAVIGATION__ORDER_FILTER_SORT,
  WLK_SUBMIT_BULK,
  WLK_SUBMIT_SINGLE,
} from '@constants/ga-events.constants';
import { ProfileOffice } from '@app/models/profile.model';
import useGlobalAlert, { Message } from '@app/core/GlobalAlertModal';
import { WarrantylinkContractData } from '@services/apis/models/warrantylink.api.model';
import msgs from '@app/locales/en';
import {
  Badge,
  Button,
  Dialog,
  Pagination,
  SelectOption,
  Tag,
} from '@ftdr/blueprint-components-react';
import { useWindowSize } from 'react-use';
import { ContentBox } from '@components/layout/ContentBox';
import ListFiltering from '@components/misc/ListFiltering';
import { StatusMenuType } from '@components/filter/OrderStatusFilter';
import { FilterOperation, FilterType } from '@constants/dashboardFilters';
import { officeTypeDictionary } from '@constants/dictionaries';
import { WarrantyLinkFilter } from '@components/filter/WarrantyLinkFilter';
import { hasContractPermissions } from '@helpers/permissions.utils';
import { Permissions } from '@constants/dictionaries';
import REText from '@components/wrappedBDS/REText';
import { getBrand } from '@helpers/brand.utils';
import WarrantyLink from '../../types/api/WarrantyLink';
import { getCurrentThemeConfig } from '@app/core/theme-config';

export interface WLKContract extends Contract {
  warrantylink: Warrantylink;
}

export interface REOrderWithBuyerAndSellerInfo extends REOrder {
  form?: WarrantylinkFormData;
}

interface WarrantyLinkTemplateProps {
  summaryView: REOrderWithBuyerAndSellerInfo[];
  isLoading: boolean;
  canSubmitContracts: boolean;
  refreshWarrantyLinkData: (request: any) => Promise<void>;
  wlkOfficeList: ProfileOffice[];
  totalContractCount: number;
}

const SKELETON_LOAD_CNT = 10;

/*
Do not set this value above 50.
The call to RE App doesn't allow size to be passed in and the contract ms has a default page size of 50.
 */
const DEFAULT_PAGE_SIZE = 30;
const PAGE_OPTIONS: SelectOption[] = [
  {
    id: '15',
    label: '15',
    value: '15',
  },
  {
    id: '30',
    label: '30',
    value: '30',
  },
];

const WarrantyLinkTemplate: React.FC<WarrantyLinkTemplateProps> = (props) => {
  const { addErrorToQueue, addSuccessToQueue } = useGlobalAlert();
  const { profile } = useContext(ProfileContext);
  const [isLegalModalActive, setIsLegalModalActive] = useState(false);
  const [selectedContractIDs, setSelectedContractIDs] = useState<string[]>([]);
  const [legalModalOffice, setLegalModalOffice] = useState<Partial<REOrder>>({
    id: 0,
    initiatingOfficeName: '',
    initiatingAgentName: '',
  });
  const [warrantyLinkContracts, setWarrantyLinkContracts] = useState<REOrder[]>([]);
  const [contractFormsErrors, setContractFormsErrors] = useState<string[]>([]);
  const [agentList, setAgentList] = useState<any[]>([]);
  const [showFilterModal, setShowFilterModal] = useState<boolean>(false);

  const [editingContractIDs, setEditingContractIDs] = useState([]);
  const [canEditWLKContracts, setCanEditWLKContracts] = useState(false);

  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(1);
  const [filterList, setFilterList] = useState<WarrantyLinkFilter[]>([]);
  const [warrantyLinkContractSearchRequest, setWarrantyLinkContractSearchRequest] = useState<any>(
    {},
  );

  const { width } = useWindowSize();
  const [isMobile, setIsMobile] = useState(width < 640);
  const wlkRequiredPermissions = [Permissions.WLKSubmit];

  const INCOMPLETE_FORM_NOTIFICATION = (invalidContractIDs: string[]): Message => {
    return {
      id: 'modal--wlk-incomplete-form',
      messageHTML: (
        <p>
          Contracts <b>{invalidContractIDs.join(', ')}</b> have incomplete data. You must have
          either buyer or seller information fully completed to submit the contract. Please update
          the information and resubmit.
        </p>
      ),
    };
  };

  useEffect(() => {
    setIsMobile(width < 640);
    hasContractPermissions(wlkRequiredPermissions).then((result) => {
      setCanEditWLKContracts(result);
    });
  }, [width]);

  useEffect(() => {
    // Deal with lookup quicker
    const warrantylinkDataMap = {};
    for (const data of props.summaryView) {
      warrantylinkDataMap[data.id] = data;
    }

    // Combine contract data with warrantylink data
    const mappedData = props.summaryView.map<REOrderWithBuyerAndSellerInfo>((c) => ({
      id: c.id,
      tableId: c.id,
      address: {
        address1: c.address?.address1,
        address2: c.address?.address2,
        city: c.address?.city,
        state: c.address?.state,
        zip: c.address?.zip,
      },
      expirationDate: c.expirationDate,
      initiatingAgentName: c.initiatingAgentName,
      initiatingOfficeName: c.initiatingOfficeName,
      initiatingAgentID: c.initiatingAgentID,
      initiatingOfficeID: c.initiatingOfficeID,
      status: c.status,
      realEstateStatus: c.realEstateStatus,
      awaitingWlkSubmission: c.awaitingWlkSubmission,
      warrantylink: {
        status: c.warrantylink.status,
        deadlineDate: c.warrantylink.deadlineDate,
        submissionDate: c.warrantylink.submissionDate,
      },
      form: c.form,
    }));

    const initialContracts = [];
    mappedData.forEach((contract) => {
      const wlkOfficeInProfile = props.wlkOfficeList.some(
        (office) => office.id === contract.initiatingOfficeID,
      );
      if (wlkOfficeInProfile) {
        initialContracts.push(contract);
      }
    });

    setWarrantyLinkContracts(initialContracts);
  }, [props.summaryView]);

  // update selected contracts based on changing filtered list, which changes based on selected filters
  useEffect(() => {
    if (selectedContractIDs.length > 0) {
      const allSubmittableContracts = warrantyLinkContracts.filter(
        (contract) =>
          ['Ready', 'Saved'].includes(contract.warrantylink.status) &&
          selectedContractIDs.includes(contract.id?.toString()),
      );

      if (allSubmittableContracts.length > 0) {
        const contractIDs = allSubmittableContracts.map((submittableContract) =>
          submittableContract.id?.toString(),
        );
        setSelectedContractIDs(contractIDs);
      } else {
        setSelectedContractIDs([]);
      }
    }
  }, [warrantyLinkContracts]);

  useEffect(() => {
    const officeIds: string[] = [];
    const agentIds: string[] = [];
    const statuses: string[] = [];

    filterList.forEach((f) => {
      if (f.operation != 'add') return;
      switch (f.type) {
        case 'office':
          officeIds.push(f.payload.office.id);
          break;
        case 'agent':
          agentIds.push(f.payload.agent.realEstateAgentID);
          break;
        case 'status':
          statuses.push(translateStatus(f.payload.status));
          break;
        default:
          console.warn('unknown filter type found', f.type);
          break;
      }
    });

    // todo: ARE-16891 add (ESTCOE) closing date range support
    const wlkSearchReq: WarrantyLink.WarrantyLinkContractSearchParams = {
      brand: getBrand(),
      offset: (page - 1) * pageSize,
      limit: pageSize,
    };
    if (officeIds.length > 0) {
      wlkSearchReq.officeId = officeIds.join(',');
    }
    if (agentIds.length > 0) {
      wlkSearchReq.agentId = agentIds.join(',');
    }
    if (statuses.length > 0) {
      wlkSearchReq.statusId = statuses.join(',');
    }
    setWarrantyLinkContractSearchRequest(wlkSearchReq);
  }, [filterList, page, pageSize]);

  useEffect(() => {
    if (
      warrantyLinkContractSearchRequest &&
      Object.keys(warrantyLinkContractSearchRequest).length > 0
    ) {
      props.refreshWarrantyLinkData(warrantyLinkContractSearchRequest).then();
    }
  }, [warrantyLinkContractSearchRequest]);

  const translateStatus = (s: string): string => {
    switch (s) {
      case 'Ready & Saved':
        return 'READY,SAVED';
      default:
        return s.toUpperCase();
    }
  };

  const addOrRemoveContractSelection = (contract: REOrderWithBuyerAndSellerInfo) => {
    if (contract) {
      const id = contract.id?.toString();
      if (selectedContractIDs.includes(id)) {
        const filteredContracts = selectedContractIDs.filter((i) => i != id);
        setSelectedContractIDs(filteredContracts);
      } else {
        setSelectedContractIDs([...selectedContractIDs, id]);
      }
    } else {
      console.error('no contract was returned from selection');
    }
  };

  /** Checks if a single form data is completed.
   * Considered completed if all fields are filled, i.e. first name, last name, email, and phone.
   */
  const isCustomerFormCompleted = (data: CustomerData): boolean => {
    return Boolean(data.firstName && data.lastName && data.email && data.phone);
  };

  /** Checks to see if contract is ready for submission based on the form data.
   * @returns true if valid for submission. Otherwise false.
   */
  const validateContractReadyForSubmission = (contractID: string): boolean => {
    const contract = props.summaryView.find((c) => c.id.toString() === contractID);

    // Contract not found
    if (!contract) {
      return false;
    }

    // Check for complete form data
    const { buyer, seller } = contract.form;
    return isCustomerFormCompleted(buyer) || isCustomerFormCompleted(seller);
  };

  // TODO: refactor so that id's are stored in a centralized data structure as opposed to one for single and one for batch
  const selectedIDs = (): string[] => {
    if (selectedContractIDs.length > 0) return selectedContractIDs;
    if (legalModalOffice.id) return [`${legalModalOffice.id}`];

    return [];
  };

  async function submitContracts(): Promise<boolean> {
    const contractIds: string[] = selectedIDs();

    if (isEmpty(contractIds)) return false;

    // Determine if there are any contracts that are not valid for submission
    // If so, display a modal to alert the user
    const invalidContractIDs: string[] = [];
    const validWarrantylinkContracts: WarrantylinkContractData[] = [];

    for (const contractId of contractIds) {
      if (!validateContractReadyForSubmission(contractId)) {
        invalidContractIDs.push(contractId);
      } else {
        const con = props.summaryView.find((c) => c.id.toString() === contractId);
        const WarrantylinkContractData = { id: con.id.toString(), form: con.form };
        validWarrantylinkContracts.push(WarrantylinkContractData);
      }
    }
    if (invalidContractIDs.length > 0) {
      addErrorToQueue(INCOMPLETE_FORM_NOTIFICATION(invalidContractIDs));

      return false;
    }

    try {
      await WarrantylinkService.submitWarrantylinkContracts(
        contractIds,
        validWarrantylinkContracts,
      );
      addSuccessToQueue(msgs.WLK_SUBMIT_SUCCESS);
      return true;
    } catch (error) {
      console.error('failed to submit wlk contracts', error);
      addErrorToQueue(msgs.WLK_SUBMIT_ERROR);
      return false;
    }
  }

  /** Saves the WLK form data and performs a refresh. Returns true on success, otherwise false. */
  async function onSaveContractForm(contract: Partial<WarrantylinkContract>): Promise<boolean> {
    try {
      const success = await WarrantylinkService.updateWarrantylinkContractForm(contract);

      // On success, refresh the contract data
      props.refreshWarrantyLinkData(warrantyLinkContractSearchRequest).then(() => {
        return success;
      });
    } catch (error) {
      console.error('failed to submit wlk contracts', error);
      addErrorToQueue(msgs.WLK_SUBMIT_ERROR);
      return false;
    }
  }

  const updateContractFormErrorsList = (error: string) => {
    if (contractFormsErrors.includes(error)) {
      const updatedErrors = contractFormsErrors.filter((string) => string !== error);
      setContractFormsErrors(updatedErrors);
    } else {
      setContractFormsErrors([...contractFormsErrors, error]);
    }
  };

  const toggleAllContractSelection = () => {
    // If we have contracts selected, we want to de-select them all. Otherwise, we are selecting all contracts.
    const hasContractsBeenSelected = selectedContractIDs.length > 0;

    // De-select all contracts
    if (hasContractsBeenSelected) {
      setSelectedContractIDs([]);
    } else {
      // Otherwise, select all valid contracts
      const allSubmittableContracts = warrantyLinkContracts.filter((contract) =>
        ['Ready', 'Saved'].includes(contract.warrantylink.status),
      );

      if (allSubmittableContracts.length > 0) {
        const contractIDs = allSubmittableContracts.map((submittableContract) =>
          submittableContract.id?.toString(),
        );

        setLegalModalOffice(allSubmittableContracts[0]);
        setSelectedContractIDs(contractIDs);
      }
    }
  };

  const handleSubmit = async () => {
    setIsLegalModalActive(false);
    const contractIds: string[] = selectedIDs();
    const success = await submitContracts();

    if (success) {
      contractIds.length > 1
        ? fireGAEvent(WLK_SUBMIT_BULK)
        : fireGAEvent(WLK_SUBMIT_SINGLE(contractIds[0]));
      await props.refreshWarrantyLinkData(warrantyLinkContractSearchRequest);
    }
  };

  const toggleEdit = (contractID, isEditing) => {
    if (isEditing) {
      setEditingContractIDs([...editingContractIDs, contractID]);
    } else {
      setEditingContractIDs(editingContractIDs.filter((id) => id !== contractID));
    }
  };

  const canBulkSubmit = () => {
    return selectedContractIDs.length > 0 && editingContractIDs.length === 0;
  };

  const canSelectAll = () => {
    return editingContractIDs.length === 0;
  };

  const getListFiltering = () => {
    return (
      <ListFiltering
        id="orders-container--filter-dropdowns"
        addFilter={onFilterChange}
        applyFilters={onFilterBatchChange}
        onFilterTextChangeHandler={(e) => null}
        officeList={props.wlkOfficeList}
        agentList={agentList}
        userRoleID={profile.roleID}
        userRoleIDType={profile.roleIDType}
        activeFilters={filterList}
        statusAlerts={null}
        isMobile={isMobile}
        totalAwaitingWlkSubmission={null}
        statusMenuType={StatusMenuType.WarrantyLink}
      />
    );
  };

  const getFilterBadge = () => {
    return filterList?.length ? (
      <Badge color="interactive" size="small" maxCount={99} className="ml-1-important">
        {filterList.length.toString()}
      </Badge>
    ) : null;
  };

  /**
   * On [add/remove] filter changes, we want to check if it is a valid change. If so, we would then make a call to refresh the dashboard contracts.
   * On refresh, we should return back to first page in pagination.
   */
  function onFilterChange(label: string, event: WarrantyLinkFilter): boolean {
    let result: boolean;

    try {
      // Handle individual event type
      switch (event.type) {
        case FilterType.OFFICE:
          result = updateOfficeFilter(event);
          break;
        case FilterType.AGENT:
          result = updateAgentFilter(event);
          break;
        case FilterType.STATUS:
          result = updateStatusFilter(event);
          break;
        case FilterType.DATE:
          result = updateDateFilter(event);
          break;
      }

      // On valid update, we want to proceed onwards to update the tags and make a new request
      if (result && event.label) {
        fireGAEvent(NAVIGATION__ORDER_FILTER_SORT(`${event.operation}: ${event.label}`));
      }
    } catch (e) {
      console.error('failed to apply filter changes', e);
    } finally {
      setPage(1);
    }

    return result;
  }

  function updateOfficeFilter(event: WarrantyLinkFilter): boolean {
    let validUpdate = false;

    const { office } = event.payload;
    const inFilter = filterList.find(
      ({ type, payload }) =>
        type === 'office' && payload.office.id === office.id && payload.office.type === office.type,
    );

    switch (event.operation) {
      case FilterOperation.ADD:
        if (!inFilter) {
          validUpdate = true;

          if (office && office.id) {
            // Update the agent list with a new set of agents for the office
            AgentApi.searchAgents(
              {
                activeInactive: 'A',
                officeId: office.id,
                officeType: office.type,
              },
              {
                tags: { source: 'Orders#updateOfficeFilter' },
              },
            )
              .then((res) => {
                if (res.agentsList.length) {
                  console.log('adding agents to filter list, count=', res.agentsList.length);
                  setAgentList([...agentList, ...res.agentsList]);
                }
              })
              .catch((e) => {
                console.error(e);
              });
          }
        }
        break;
      case FilterOperation.REMOVE:
        if (inFilter) {
          const tags = filterList.filter(
            ({ type, payload }) =>
              !(
                type === 'office' &&
                payload.office.id === office.id &&
                payload.office.type === office.type
              ),
          );
          validUpdate = true;

          // Update the agent list and filter tags by removing agents under the office removed
          console.log('removing agents from filter list');
          setAgentList(
            agentList.filter(
              (a) =>
                !(a.officeID === office.id && officeTypeDictionary[a.agentType] === office.type),
            ),
          );
          setFilterList(
            tags.filter(
              (i) =>
                !(
                  i.type === 'agent' &&
                  i.payload.agent.officeID === office.id &&
                  officeTypeDictionary[i.payload.agent.agentType] === office.type
                ),
            ),
          );
        } else {
          console.warn('attempted to remove, but cannot find it');
        }
        break;
    }

    return validUpdate;
  }

  function updateAgentFilter(event: WarrantyLinkFilter): boolean {
    let validUpdate = false;

    const { agent } = event.payload;
    const inFilter = filterList.find(
      ({ type, payload }) =>
        type === 'agent' &&
        payload.agent.realEstateAgentID === agent.realEstateAgentID &&
        payload.agent.agentType === agent.agentType,
    );

    switch (event.operation) {
      case FilterOperation.ADD:
        if (!inFilter) {
          setFilterList([...filterList, event]);
          validUpdate = true;
        }
        break;
      case FilterOperation.REMOVE:
        if (inFilter) {
          setFilterList(
            filterList.filter(
              ({ type, payload }) =>
                !(
                  type === 'agent' &&
                  payload.agent.realEstateAgentID === agent.realEstateAgentID &&
                  payload.agent.agentType === agent.agentType
                ),
            ),
          );
          validUpdate = true;
        } else {
          console.warn('attempted to remove, but cannot find it');
        }
        break;
    }

    return validUpdate;
  }

  function updateDateFilter(event: WarrantyLinkFilter): boolean {
    let validUpdate = false;

    const { date } = event.payload;
    const inFilter = filterList.find(({ type }) => type === 'date');

    switch (event.operation) {
      case FilterOperation.ADD: {
        // We always update the date range if add occurs; replacing the existing one; as there should only be 1 date range filter
        const startDate = formatDateToISO8601(date.start);
        const endDate = formatDateToISO8601(date.end);
        const validDates = Boolean(startDate && endDate);
        if (validDates) {
          setFilterList([...filterList.filter((i) => i.type !== 'date'), event]);
          validUpdate = true;
        }
        break;
      }
      case FilterOperation.REMOVE: {
        if (inFilter) {
          setFilterList(filterList.filter((i) => i.type !== 'date'));
          validUpdate = true;
        } else {
          console.warn('attempted to remove date, but cannot find it');
        }
        break;
      }
    }

    return validUpdate;
  }

  function updateStatusFilter(event: WarrantyLinkFilter): boolean {
    let validUpdate = false;

    const { status } = event.payload;
    const inFilter = filterList.find(
      ({ type, payload }) => type === 'status' && payload.status === status,
    );

    switch (event.operation) {
      case FilterOperation.ADD:
        if (!inFilter) {
          setFilterList([...filterList, event]);
          validUpdate = true;
        }
        break;
      case FilterOperation.REMOVE:
        if (inFilter) {
          setFilterList(
            filterList.filter(
              ({ type, payload }) => !(type === 'status' && payload.status === status),
            ),
          );
          validUpdate = true;
        }
        break;
    }

    return validUpdate;
  }

  /**
   * On [add/remove] filter changes, pass in filter list from the modal.
   * Overwrite the existing filters with the new list, fire GA event, update agent list.
   */
  function onFilterBatchChange(events: WarrantyLinkFilter[]): boolean {
    const updatedAgentList = [];

    try {
      // remove all the office type tags then append with our new list
      const tempFilterList = filterList.filter((filter) => filter.type !== FilterType.OFFICE);

      setFilterList([...tempFilterList, ...events]);

      events.forEach((e) => {
        // Update the agent list with a new set of agents for the office
        fireGAEvent(NAVIGATION__ORDER_FILTER_SORT(`${e.operation}: ${e.label}`));
        if (e.payload.office && e.payload.office.id !== '') {
          AgentApi.searchAgents(
            {
              activeInactive: 'A',
              officeId: e.payload.office.id,
              officeType: e.payload.office.type,
            },
            {
              tags: { source: 'Orders#updateOfficeFilter' },
            },
          )
            .then((res) => {
              if (res.agentsList.length) {
                console.log('adding agents to filter list, count=', res.agentsList.length);
                updatedAgentList.push(...res.agentsList);
                setAgentList(updatedAgentList);
              }
            })
            .catch((err) => {
              console.error(err);
            });
        }
      });
    } catch (e) {
      console.error('failed to apply filter changes', e);
      return false;
    }

    return true;
  }

  return (
    <ContentBox
      title="WarrantyLink&trade;"
      leftContent={
        !isMobile && (
          <div className="w-48 min-w-48 mr-4">
            <REText variant="heading-06" className="my-2">
              Filter WarrantyLink
            </REText>
            {getListFiltering()}
          </div>
        )
      }
    >
      <>
        <div id="wlk-sticky-container" className={getCurrentThemeConfig().wlkStickyContainerClass}>
          {isMobile ? (
            <Button
              id="filter-wlk-orders-button"
              label="Filter WarrantyLink"
              shape="rounded"
              variant="outlined"
              endIcon={getFilterBadge()}
              onClick={() => setShowFilterModal(true)}
              width="full"
              labelAlign="center"
              className={getCurrentThemeConfig().wlkButtonClass}
            />
          ) : (
            <div className="md:mb-4">
              <TagGroup>
                {filterList.map((filter) => (
                  <Tag
                    id={`wlk_contract_status_filter__${filter.label}`}
                    key={filter.label}
                    className="ml-4"
                    removable
                    color="interactive"
                    onClick={(e) => {
                      filter.operation = FilterOperation.REMOVE;
                      onFilterChange(filter.label, filter);
                    }}
                  >
                    {filter.label}
                  </Tag>
                ))}
              </TagGroup>
            </div>
          )}

          <div
            className={classNames(['flex mb-4', !props.canSubmitContracts && 'invisible'])}
            style={{ alignItems: 'end', justifyContent: 'space-between' }}
          >
            {!isMobile && canEditWLKContracts && (
              <>
                <Button
                  id={
                    selectedContractIDs.length > 0
                      ? 'wlk_contract__select_all'
                      : 'wlk_contract__deselect_all'
                  }
                  className={`${getCurrentThemeConfig().wlkButtonClass} button button-primary underline floatLeft -mb-2`}
                  variant="ghost"
                  color="interactive"
                  size="small"
                  label={selectedContractIDs.length > 0 ? 'Deselect All' : 'Select All'}
                  onClick={toggleAllContractSelection}
                  disabled={!canSelectAll()}
                />
                <Button
                  id="wlk_contract__submit_selected"
                  className={`${getCurrentThemeConfig().wlkButtonClass} button button-primary sm-only:w-full floatRight`}
                  color="interactive"
                  size="medium"
                  label="Submit"
                  disabled={!canBulkSubmit()}
                  onClick={() => {
                    setIsLegalModalActive(true);
                  }}
                />
              </>
            )}
          </div>
        </div>

        {warrantyLinkContracts?.length === 0 && !props.isLoading && (
          <div className="mt-3">
            <strong>No Results Found</strong>
          </div>
        )}

        {/*TODO: ARE-9273 This uses old SF accordion, change this to BDS. causes props.onClick black magic*/}
        {/* Contracts accordion list */}
        <Accordion className="mt-3">
          {props.isLoading
            ? Array(SKELETON_LOAD_CNT)
                .fill(null)
                .map((item, idx) => <AccordionItemWarranty key={idx} skeleton={true} />)
            : warrantyLinkContracts.map((wlkContract, idx) => (
                <AccordionItemWarranty
                  filteredList={props.summaryView}
                  key={idx}
                  id={`wlk_contract__${idx}`}
                  selectedContracts={selectedContractIDs}
                  contract={wlkContract}
                  canSubmit={props.canSubmitContracts}
                  address={`${addressToString(
                    wlkContract?.address?.address1,
                    wlkContract?.address?.address2,
                    wlkContract?.address?.city,
                    wlkContract?.address?.state,
                    wlkContract?.address?.zip,
                  )}`}
                  submissionDeadline={formatDateFromString(wlkContract?.warrantylink?.deadlineDate)}
                  submissionDate={formatDateFromString(wlkContract?.warrantylink?.submissionDate)}
                  contractNumber={wlkContract.id}
                  contractId={wlkContract.id}
                  agent={wlkContract.initiatingAgentName}
                  office={wlkContract.initiatingOfficeName}
                  buyer={props.summaryView?.find((sv) => sv.id === wlkContract.id)?.form?.buyer}
                  seller={props.summaryView?.find((sv) => sv.id === wlkContract.id)?.form?.seller}
                  onSaveForm={onSaveContractForm}
                  updateErrors={updateContractFormErrorsList}
                  status={wlkContract?.warrantylink?.status}
                  onCheckboxClick={addOrRemoveContractSelection}
                  toggleEdit={toggleEdit}
                  isMobile={isMobile}
                  canEditWLKContracts={canEditWLKContracts}
                />
              ))}
        </Accordion>

        {isMobile && canEditWLKContracts && (
          <div className="sticky-container-bottom extended-border-invisible force-background-white border-top-faded force-margin-x-wide flex items-center">
            <div className="m-auto">
              <Button
                id={
                  selectedContractIDs.length > 0
                    ? 'wlk_contract__select_all'
                    : 'wlk_contract__deselect_all'
                }
                className="underline"
                variant="ghost"
                color="interactive"
                size="small"
                label={selectedContractIDs.length > 0 ? 'Deselect All' : 'Select All'}
                onClick={toggleAllContractSelection}
                disabled={!canSelectAll()}
                width="full"
                labelAlign="center"
                shape={getCurrentThemeConfig().buttonThemeShape}
              />
            </div>
            <div className="m-auto">
              <Button
                id="wlk_contract__submit_selected"
                color="interactive"
                size="medium"
                label="Submit"
                disabled={!canBulkSubmit()}
                onClick={() => {
                  setIsLegalModalActive(true);
                }}
                width="full"
                labelAlign="center"
                shape={getCurrentThemeConfig().buttonThemeShape}
              />
            </div>
          </div>
        )}

        <Pagination
          itemsPerPage={pageSize}
          itemsPerPageOptions={PAGE_OPTIONS}
          onItemsPerPageChange={(item) => setPageSize(Number(item.value))}
          totalItems={props.totalContractCount}
          page={page}
          onPageChange={setPage}
          hideViewAll={true}
        />

        <Dialog
          id="filter-wlk-modal"
          header="Filter Orders"
          modal={true}
          onClose={() => setShowFilterModal(false)}
          open={showFilterModal && isMobile}
          actionsAlign="right"
          actions={[
            <Button
              key="action"
              size="small"
              label="Done"
              onClick={() => setShowFilterModal(false)}
              className={getCurrentThemeConfig().btnPillShapedPrimary}
            />,
          ]}
        >
          <div className="min-w-80">{getListFiltering()}</div>
        </Dialog>

        {legalModalOffice && (
          <ModalLegalConfirmation
            isActive={isLegalModalActive}
            office={legalModalOffice.initiatingOfficeName}
            firstName={profile.firstName}
            lastName={profile.lastName}
            onClose={() => setIsLegalModalActive(false)}
            onSubmit={handleSubmit}
          />
        )}
      </>
    </ContentBox>
  );
};

export default WarrantyLinkTemplate;
